import { useId } from 'react'
import { Field, FieldGrid, TextArea, CodeSelect } from './fields'
import { SeverityChip } from '../vocab'
import type { IncidentVocabulary } from '../api'

/**
 * Step 2 · Narrative — what happened in words, and how bad it is.
 *
 * ## Severity and priority are not the same question
 *
 * Severity is about the EVENT: how bad the thing that happened was. Priority is about the
 * DESK: how soon somebody must pick it up. A CRITICAL incident already fully documented
 * and referred can sit at P3; a MEDIUM one blocking a queue right now is P1. Collapsing
 * them into one number is how a desk ends up sorting by the wrong axis.
 *
 * Severity is the one field this step requires, because it is what the register sorts by
 * and what the CRITICAL alert fires on.
 */
export function StepNarrative({
  vocab, value, onChange, errors,
}: {
  vocab: IncidentVocabulary | null
  value: {
    description: string | null
    severityCode: string | null
    priorityCode: string | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
}) {
  const ids = useId()

  return (
    <div className="space-y-4">
      <Field label="What happened" htmlFor={`${ids}-desc`} error={errors.description}
        hint="Plain description. What you saw, who was involved, what it stopped.">
        <TextArea id={`${ids}-desc`} rows={7} max={4000}
          value={value.description ?? ''}
          onChange={(v) => onChange({ description: v || null })} />
      </Field>

      <FieldGrid>
        <Field label="Severity" htmlFor={`${ids}-sev`} error={errors.severityCode ?? errors.severity}
          hint="Required. How bad the EVENT was — not how urgently the desk must act.">
          <CodeSelect id={`${ids}-sev`} codes={vocab?.severities} value={value.severityCode}
            onChange={(v) => onChange({ severityCode: v })} emptyLabel="Choose a severity…" />
          {value.severityCode && (
            <div className="mt-2"><SeverityChip code={value.severityCode} /></div>
          )}
        </Field>

        <Field label="Priority" htmlFor={`${ids}-pri`} error={errors.priorityCode ?? errors.priority}
          hint="How soon the desk must pick it up. Defaults to P3 — standard.">
          <CodeSelect id={`${ids}-pri`} codes={vocab?.priorities} value={value.priorityCode}
            onChange={(v) => onChange({ priorityCode: v })} emptyLabel="P3 — standard (default)" />
        </Field>
      </FieldGrid>

      <p className="text-erp-xs text-muted-foreground/80 border-l-2 border-border pl-3">
        Nothing here says whether the report is <em>true</em>. That is verification, and it
        is a separate record set by somebody checking it against evidence — an incident can
        be resolved and still unverified, and the desk must never lose the difference.
      </p>
    </div>
  )
}
